import type { ComponentProps } from 'preact';
import type { PlaceOption } from '../../../data/places';
import { useWidgetSettings } from '../../../config/WidgetSettingsContext';
import { SvgIcon } from '../Icon/SvgIcon';
import {
  AutocompleteInput,
  type AutocompleteInputItem,
} from './AutocompleteInput';
import styles from './LocationAutocompleteInput.module.css';

interface CurrentLocationAction {
  onSelect: () => void | Promise<void>;
  label?: string;
}

interface LocationAutocompleteInputProps extends Omit<
  ComponentProps<typeof AutocompleteInput>,
  'items' | 'onSelectItem'
> {
  options?: PlaceOption[];
  currentLocationAction?: CurrentLocationAction;
  onSelectOption?: (option: PlaceOption) => void;
  disableCurrentLocation?: boolean;
}

/** Wraps a `AutocompleteInput` to provide location-specific functionality */
export function LocationAutocompleteInput({
  options = [],
  currentLocationAction,
  onSelectOption,
  disableCurrentLocation,
  value,
  ...props
}: LocationAutocompleteInputProps) {
  const { disableMyLocation } = useWidgetSettings();
  const showCurrentLocation =
    !disableMyLocation &&
    !disableCurrentLocation &&
    !value &&
    currentLocationAction;
  const items = [
    ...(showCurrentLocation
      ? [createCurrentLocationItem(currentLocationAction)]
      : []),
    ...options.map(createLocationItem),
  ];

  return (
    <AutocompleteInput
      {...props}
      value={value}
      items={items}
      onSelectItem={(item) => {
        const selectedOption = options.find(
          (option) => option.key === item.key,
        );

        if (selectedOption) {
          onSelectOption?.(selectedOption);
        }
      }}
    />
  );
}

function createCurrentLocationItem({
  onSelect,
  label = 'My location',
}: CurrentLocationAction): AutocompleteInputItem {
  return {
    key: 'current-location',
    value: label,
    updateInputOnSelect: false,
    onSelect,
    content: (
      <>
        <SvgIcon fill="black" size={24} name="locationArrow" />
        <div className={styles.itemText}>
          <strong>{label}</strong>
        </div>
      </>
    ),
  };
}

function createLocationItem(option: PlaceOption): AutocompleteInputItem {
  return {
    key: option.key,
    value: option.value,
    content: (
      <>
        {getLocationOptionIcon(option.type)}
        <div className={styles.itemText}>
          <strong>{option.primaryText ?? option.value}</strong>
          {option.label && <span>{option.label}</span>}
        </div>
        {option.distance && (
          <span className={styles.distance}>{option.distance}</span>
        )}
      </>
    ),
  };
}

function getLocationOptionIcon(type: PlaceOption['type']) {
  if (type === 'stop') {
    return <SvgIcon name="busLocation" size={24} />;
  }

  return <SvgIcon name="mapPin" size={24} fill="black" />;
}
